import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import {
  UpdateTariffElementModel,
  getUpdateTariffDefaultValues,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.form-default-values';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { PriceComponentModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/price-component.model';
import { TariffElementModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff-element.model';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useEffect, useMemo, useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { TariffUpdateFormController } from '@app/feature/tariff/tariff-update/tariff-update-form/tariff-update-form.controller';
import {
  RangeUIModel,
  UpdateTariffContextProvider,
  useTariffUIContext,
} from '@app/feature/tariff/tariff.context';
import { useCpmsBoUpdateTariffMutation } from '@service/cpms-bo-tariff.service';

interface TariffUpdateControllerProps {
  tariff: TariffModel;
  onSuccessfulUpdate: () => void;
  onCancel: () => void;
}

const isElementEmpty = (element: UpdateTariffElementModel) => {
  if (element.restrictions) {
    return false;
  }
  return !element.priceComponents?.some(
    (p) => p.price !== undefined || p.stepSize !== undefined,
  );
};

const hasRestrictions = (element: UpdateTariffElementModel) => {
  if (!element.restrictions) {
    return false;
  }
  return (
    element.restrictions.dayOfWeek ||
    element.restrictions.startTime ||
    element.restrictions.endTime
  );
};

export const TariffUpdateController = ({
  tariff,
  onSuccessfulUpdate,
  onCancel,
}: TariffUpdateControllerProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [handleUpdate] = useCpmsBoUpdateTariffMutation();
  const {
    flatValue,
    setIsPriceRange,
    setFlatValue,
    setFirstRange,
    setSecondRange,
  } = useTariffUIContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  const handleSubmit: SubmitHandlerWithDirtyFields<UpdateTariffModel> = (
    data,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const isEmptyPriceComponent = (priceComponent: PriceComponentModel) => {
      return !priceComponent.price && !priceComponent.stepSize;
    };

    const cleanElements = data.tariff.elements?.reduce((accEl, el) => {
      if (
        !el.priceComponents ||
        el.priceComponents.length === 0 ||
        el.priceComponents.filter((p) => !isEmptyPriceComponent(p)).length === 0
      ) {
        return accEl;
      }

      const priceComponents = el.priceComponents.reduce((acc, pr) => {
        if (!pr.price || !pr.stepSize) {
          return acc;
        }

        if (pr.type === TariffDimensionType.FLAT) {
          // on enlève le flat s'il a été enlevé
          if (!flatValue) {
            return acc;
          }
          // on met à jour la valeur si besoin
          if (flatValue) {
            return [
              ...acc,
              {
                price: flatValue,
                stepSize: 1,
                type: TariffDimensionType.FLAT,
              },
            ];
          }
        }

        return [
          ...acc,
          {
            price: pr.price,
            stepSize: pr.stepSize,
            type: pr.type,
          },
        ];
      }, [] as PriceComponentModel[]);

      if (
        flatValue &&
        !priceComponents.some((p) => p.type === TariffDimensionType.FLAT)
      ) {
        priceComponents.push({
          price: flatValue,
          stepSize: 1,
          type: TariffDimensionType.FLAT,
        });
      }

      return [
        ...accEl,
        {
          ...el,
          priceComponents,
        },
      ];
    }, [] as TariffElementModel[]);

    handleUpdate({
      ...data.tariff,
      elements: cleanElements,
      id: tariff.id,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.tariff-update.success'));
        onSuccessfulUpdate();
      })
      .catch(() => {
        toast.error(t('$.tariff-update.error'));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  const handleError: SubmitErrorHandler<UpdateTariffModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.tariff-update.error', { error: t('global.unknown') }));
  };

  const data = useMemo(() => {
    const defaultElement: UpdateTariffElementModel = {
      priceComponents: [
        {
          type: TariffDimensionType.ENERGY,
          price: undefined,
          stepSize: undefined,
        },
        {
          type: TariffDimensionType.TIME,
          price: undefined,
          stepSize: undefined,
        },
        {
          type: TariffDimensionType.PARKING_TIME,
          price: undefined,
          stepSize: undefined,
        },
        {
          type: TariffDimensionType.FLAT,
          price: undefined,
          stepSize: undefined,
        },
      ],
    };

    const d = getUpdateTariffDefaultValues(tariff);

    if (!d?.tariff?.elements?.[0]) {
      toast.warn(
        'Le tarif devrait avoir au moins un élément. La mise à jour pourrait ne pas fonctionner.',
      );
      Logger.warn(
        'Tariff update should have at least one element. Possibly an outdated tariff.',
      );
    }

    if (d?.tariff?.elements && d.tariff.elements.length > 4) {
      toast.warn(
        'Le tarif a plus de 4 éléments. La mise à jour pourrait ne pas fonctionner.',
      );
      Logger.warn(
        'Tariff have more than 4 elements. Possibly an outdated tariff.',
      );
    }

    const elements = d.tariff.elements ?? [];

    const weekdayOrder = [
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
      'SUNDAY',
    ];

    // 1️⃣ Grouper par dayOfWeek (ordre des jours normalisé)
    const groups = new Map<
      string,
      { keyDays: string[]; items: UpdateTariffElementModel[] }
    >();

    for (const el of elements) {
      const days = el.restrictions?.dayOfWeek ?? [];
      const sortedDays = [...days].sort(
        (a, b) => weekdayOrder.indexOf(a) - weekdayOrder.indexOf(b),
      );
      const key = JSON.stringify(sortedDays);
      if (!groups.has(key)) {
        groups.set(key, { keyDays: sortedDays, items: [] });
      }
      groups.get(key)!.items.push(el);
    }

    // 2️⃣ Trier les groupes selon le premier jour
    const groupedArray = Array.from(groups.values()).sort((g1, g2) => {
      const idx1 = g1.keyDays.length
        ? weekdayOrder.indexOf(g1.keyDays[0]!)
        : Infinity;
      const idx2 = g2.keyDays.length
        ? weekdayOrder.indexOf(g2.keyDays[0]!)
        : Infinity;
      return idx1 - idx2;
    });

    // 3️⃣ Construire les éléments finaux en paires
    const newElements: UpdateTariffElementModel[] = [];

    for (const group of groupedArray) {
      const { items } = group;
      if (items.length === 1) {
        // un seul élément → ajouter un default après
        newElements.push(items[0]!, structuredClone(defaultElement));
      } else {
        // plusieurs → ajouter tous (2 max)
        newElements.push(...items.slice(0, 2));
      }
    }

    // 4️⃣ Compléter à 4 éléments si nécessaire
    while (newElements.length < 4) {
      newElements.push(structuredClone(defaultElement));
    }

    // 5️⃣ Limiter à 4
    d.tariff.elements = newElements.slice(0, 4);

    return d;
  }, [tariff]);

  useEffect(() => {
    if (data.tariff.elements?.length !== 4) {
      toast.error('Une erreur est survenue lors de la modification du tarif.');
      return;
    }

    const firstElement = data.tariff.elements[0]!;
    const secondElement = data.tariff.elements[1]!;
    const thirdElement = data.tariff.elements[2]!;
    const fourthElement = data.tariff.elements[3]!;

    if (
      data.tariff.elements.filter((e) => !isElementEmpty(e)).length >= 2 ||
      data.tariff.elements.some(hasRestrictions)
    ) {
      setIsPriceRange(true);
    } else {
      setIsPriceRange(false);
    }

    // chercher s'il y a un composant FLAT
    const flatComponent = firstElement.priceComponents?.find(
      (pc) => pc.type === TariffDimensionType.FLAT,
    );
    if (flatComponent) {
      setFlatValue(flatComponent.price);
    }

    // initialiser firstRange
    const firstRange: RangeUIModel = {
      hasDoubleTimeSlot:
        !!firstElement.restrictions?.startTime ||
        !!firstElement.restrictions?.endTime,
      daysOfWeek: firstElement.restrictions?.dayOfWeek,
      firstTimeSlot: {
        startTime: firstElement.restrictions?.startTime,
        endTime: firstElement?.restrictions?.endTime,
        hasParkingPrice: !!firstElement?.priceComponents?.find(
          (p) =>
            p.type === TariffDimensionType.PARKING_TIME &&
            p.price !== undefined &&
            p.stepSize !== undefined,
        ),
      },
      secondTimeSlot: {
        startTime: secondElement.restrictions?.startTime,
        endTime: secondElement.restrictions?.endTime,
        hasParkingPrice: !!secondElement?.priceComponents?.find(
          (p) =>
            p.type === TariffDimensionType.PARKING_TIME &&
            p.price !== undefined &&
            p.stepSize !== undefined,
        ),
      },
    };
    setFirstRange(firstRange);

    // initialiser secondRange
    const secondRange: RangeUIModel = {
      hasDoubleTimeSlot:
        !!thirdElement.restrictions?.startTime ||
        !!thirdElement.restrictions?.endTime,
      daysOfWeek: thirdElement.restrictions?.dayOfWeek,
      firstTimeSlot: {
        startTime: thirdElement.restrictions?.startTime,
        endTime: thirdElement?.restrictions?.endTime,
        hasParkingPrice: !!thirdElement?.priceComponents?.find(
          (p) =>
            p.type === TariffDimensionType.PARKING_TIME &&
            p.price !== undefined &&
            p.stepSize !== undefined,
        ),
      },
      secondTimeSlot: {
        startTime: fourthElement.restrictions?.startTime,
        endTime: fourthElement?.restrictions?.endTime,
        hasParkingPrice: !!fourthElement?.priceComponents?.find(
          (p) =>
            p.type === TariffDimensionType.PARKING_TIME &&
            p.price !== undefined &&
            p.stepSize !== undefined,
        ),
      },
    };
    setSecondRange(secondRange);
  }, [data, setFlatValue, setIsPriceRange, setFirstRange, setSecondRange]);

  return (
    <UpdateTariffContextProvider data={data as UpdateTariffModel}>
      <TariffUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateTariffContextProvider>
  );
};
